import React from 'react';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { useReduxDispatch } from '../../../app/store';
import { AccountMenuButton } from '../../account/AccountMenuButton';
import { resetMeterValues, setFirmwareUpdateStatus, setMeterValues } from '../../smart-amp/evseStreamSlice';
import { selectUser } from '../../user/userSlice';
import { EvseType } from '../types';
import {
  ChargeSimulationType,
  DeferralType,
  EVSE_SIMULATION_SUFFIX,
  FirmwareSimulationType,
  getSimulationBase,
  isDeviceSimulationType,
  selectChargeSimulationType,
  selectDeferralType,
  selectEvseType,
  selectFirmwareSimulationType,
  selectTariffSimulationActive,
  setChargeSimulationType,
  setDeferralType,
  setEvseType,
  setFirmwareSimulationType,
  setTariffSimulationActive,
  syncSmartAmpState,
} from './evChargerSlice';

const FW_SIM_PAYLOADS: Record<FirmwareSimulationType, Parameters<typeof setFirmwareUpdateStatus>[0]> = {
  None: { status: null, cloudStatus: null, firmwareVersion: null },
  Pending: { status: null, cloudStatus: 'Pending' },
  Downloading: { status: 'Downloading', cloudStatus: 'InProgress' },
  Installing: { status: 'Installing', cloudStatus: 'InProgress' },
  Success: { status: 'Installed', cloudStatus: 'Success', firmwareVersion: '2.0.0' },
  DownloadFailed: { status: 'DownloadFailed', cloudStatus: 'Failed' },
  InstallationFailed: { status: 'InstallationFailed', cloudStatus: 'Failed' },
  FailedChargerNotConnected: { status: null, cloudStatus: 'FailedChargerNotConnected' },
};

/* istanbul ignore next -- @preserve */
export function SimulationDeveloperTool() {
  const user = useSelector(selectUser);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const chargeSimulationType = useSelector(selectChargeSimulationType);
  const tariffSimulationActive = useSelector(selectTariffSimulationActive);
  const deferralType = useSelector(selectDeferralType);
  const firmwareSimulationType = useSelector(selectFirmwareSimulationType);
  const evseType = useSelector(selectEvseType);
  const dispatch = useReduxDispatch();

  const deviceSuffix = EVSE_SIMULATION_SUFFIX[evseType];
  const isSmartAmpActive = isDeviceSimulationType(chargeSimulationType);
  const baseStatus = getSimulationBase(chargeSimulationType);

  // SmartAmp is always active for Charging Derated, EVSE Stopped by Smart Amp, and Charger Error.
  // No status has a toggleable SmartAmp variant anymore.
  const SMART_AMP_TOGGLEABLE: string[] = [];
  const SMART_AMP_ALWAYS_ON = ['Unavailable_Disconnect', 'Suspended_EVSE', 'Faulted'];
  const isSmartAmpToggleable = SMART_AMP_TOGGLEABLE.includes(baseStatus);
  const isSmartAmpAlwaysOn = SMART_AMP_ALWAYS_ON.includes(baseStatus);
  const visibleSmartAmpChecked = isSmartAmpAlwaysOn ? true : isSmartAmpActive;
  // Disable toggle when: no SmartAmp variant exists, or SmartAmp is always on (Charging Derated, Charger Error).
  const isSmartAmpToggleDisabled = !isSmartAmpToggleable;

  function handleBaseChange(base: string) {
    // Charging Derated only has a SmartAmp variant — always force it on.
    if (SMART_AMP_ALWAYS_ON.includes(base) && base !== 'Faulted') {
      dispatch(setChargeSimulationType(`${base}${deviceSuffix}` as ChargeSimulationType));
      dispatch(syncSmartAmpState());
      return;
    }
    const next = isSmartAmpActive && SMART_AMP_TOGGLEABLE.includes(base) ? `${base}${deviceSuffix}` : base;
    dispatch(setChargeSimulationType(next));
    dispatch(syncSmartAmpState());
  }

  function handleSmartAmpToggle(checked: boolean) {
    if (!isSmartAmpToggleable) return;
    if (checked) {
      dispatch(setChargeSimulationType(`${baseStatus}${deviceSuffix}`));
    } else {
      dispatch(setChargeSimulationType(baseStatus));
    }
  }

  const DEFERRED_STATES = ['Unavailable_Disconnect', 'Suspended_EVSE'];
  const showDeferralType = evseType === 'SmartAmp' && DEFERRED_STATES.includes(baseStatus);

  const STATE_DESCRIPTIONS: Record<string, string> = {
    None: 'No simulation active. Live API data is used.',
    Available: 'Charger ready for operation. Gun is unplugged and there are no errors preventing operation.',
    Preparing:
      'Active power flowing from grid to EV due to an active non-zero schedule. Only available when a schedule is set up. (Automatic mode)',
    Charging:
      'Active power flowing from grid to EV due to a manual override. Only available when a schedule is set up. (Manual mode)',
    Unavailable_Disconnect:
      'Power flowing but derated — SmartAmp or SmartPlug limits the current. The homeowner must be informed of the exact reason (load or temperature). SmartAmp/SmartPlug is always on.',
    Finishing:
      'EVSE has suspended charging due to a scheduled blocked-charge window. Power delivery is interrupted. Only shown when a charging schedule exists.',
    SuspendedEVSE:
      'User stopped/paused charging from the SE Home app. Start Charging button visible. Only available when no schedule is set up.',
    Suspended_EVSE:
      'Charging paused by eMobility/SmartAmp (SUA or Thermal)/SmartPlug (Thermal). Start Charging button is visible. SmartAmp/SmartPlug is always on.',
    SuspendedEV:
      'EV suspended charging (target SoC reached, S3 button press, or EV-side stop). Power delivery interrupted. Start button is not visible.',
    Faulted:
      'Severe fault preventing power flow. Not all faults lead to this state (e.g. CT1/CT2 faults do not). SmartAmp is always shown as active.',
    Wifi_Faulted:
      'Cloud and charger are not communicating — OCPP heartbeat not received for >30 s. WiFi offline indicator shown.',
  };

  function handleDeferralTypeChange(value: string) {
    dispatch(setDeferralType(value as DeferralType));
    dispatch(syncSmartAmpState());
  }

  function handleFirmwareSimTypeChange(value: string) {
    const fwSimType = value as FirmwareSimulationType;
    dispatch(setFirmwareSimulationType(fwSimType));
    dispatch(setFirmwareUpdateStatus(FW_SIM_PAYLOADS[fwSimType]));
  }

  function handleTariffToggle(checked: boolean) {
    dispatch(setTariffSimulationActive(checked));
    if (checked) {
      dispatch(
        setMeterValues({
          amountUsage: 440.0,
          currency: '$',
          kwhConsumed: 0,
          kwInstantaneousPower: null,
          maxAmps: null,
          chargingAmps: null,
        })
      );
    } else {
      dispatch(resetMeterValues());
    }
  }

  return (
    <>
      {user.role === 'SUPER_ADMIN' && localStorage.getItem('developerToolsEnabled') === 'true' && (
        <AccountMenuButton data-testid="state-simulation-button" onClick={onOpen} includeBottomDivider={false}>
          <Text fontSize={'16px'} fontWeight="normal">
            State Simulation
          </Text>
        </AccountMenuButton>
      )}

      <Drawer placement={'bottom'} onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent maxH="calc(100dvh - env(safe-area-inset-top, 0px))" pb="env(safe-area-inset-bottom, 0px)">
          <DrawerCloseButton data-testid="simulation-close-button" />
          <DrawerHeader borderBottomWidth="1px">Charger Simulation Settings</DrawerHeader>
          <DrawerBody overflowY="auto">
            <Box mb={4}>
              <Text mb={2} fontWeight="bold">
                EVSE Type:
              </Text>
              <RadioGroup
                onChange={(value) => {
                  dispatch(setEvseType(value as EvseType));
                  dispatch(setDeferralType(value === 'SmartAmp' ? 'load' : 'temp'));
                  dispatch(syncSmartAmpState());
                }}
                value={evseType}
              >
                <Stack direction="row" spacing={6}>
                  <Radio value="SmartAmp">SmartAmp</Radio>
                  <Radio data-testid="simulation-type-smartplug" value="SmartPlug">
                    SmartPlug{' '}
                    <Text as="span" fontSize="xs">
                      (NEMA 14-50)
                    </Text>
                  </Radio>
                </Stack>
              </RadioGroup>
            </Box>
            <Flex align="center" justify="space-between" my={2} mb={4}>
              <Text fontWeight="bold">
                {evseType} Active {evseType === 'SmartAmp' ? '(DeratingSUA=1)' : ''}
              </Text>
              <Switch
                isChecked={visibleSmartAmpChecked}
                isDisabled={isSmartAmpToggleDisabled}
                onChange={(e) => handleSmartAmpToggle(e.target.checked)}
                size="lg"
                variant="solarGreen"
              />
            </Flex>
            <Flex align="center" justify="space-between" my={2} mb={4}>
              <Text fontWeight="bold">Tariff Active (amountUsage=$440.00)</Text>
              <Switch
                isChecked={tariffSimulationActive}
                onChange={(e) => handleTariffToggle(e.target.checked)}
                size="lg"
                variant="solarGreen"
              />
            </Flex>
            <Text mb={2}>Select a charge status to simulate:</Text>
            <RadioGroup onChange={handleBaseChange} value={baseStatus}>
              <Stack>
                <Radio value="None">None</Radio>
                <Radio value="Available">Charger Disconnected / Available</Radio>
                <Radio value="Preparing">Charging Ongoing (Automatic)</Radio>
                <Radio value="Charging">Charging Ongoing (Manual)</Radio>
                <Radio data-testid="simulation-status-derated" value="Unavailable_Disconnect">
                  Charging Derated
                </Radio>
                <Radio value="Finishing">Waiting for Scheduled Charging to Start</Radio>
                <Radio value="SuspendedEVSE">User Stopped Charging from SE Home App</Radio>
                <Radio data-testid="simulation-status-stopped" value="Suspended_EVSE">
                  EVSE Stopped Charging due to Smart Amp/Plug
                </Radio>
                <Radio value="SuspendedEV">EV Stopped Charging</Radio>
                <Radio value="Faulted">Charger Error</Radio>
                <Radio value="Wifi_Faulted">Charger Offline</Radio>
              </Stack>
            </RadioGroup>

            {STATE_DESCRIPTIONS[baseStatus] && (
              <Box mt={3} p={3} bg="gray.50" borderRadius="md" _dark={{ bg: 'gray.700' }}>
                <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }}>
                  {STATE_DESCRIPTIONS[baseStatus]}
                </Text>
              </Box>
            )}

            {showDeferralType && (
              <Box mt={4}>
                <Text mb={2} fontWeight="bold">
                  Deferral reason (affects SmartAmp info field):
                </Text>
                <RadioGroup onChange={handleDeferralTypeChange} value={deferralType}>
                  <Stack>
                    <Radio value="load">Load-related (DeratingSUA=1, DeratingThermal=0)</Radio>
                    <Radio value="temp">Temperature-related (DeratingSUA=0, DeratingThermal=1)</Radio>
                    <Radio value="both">Both (DeratingSUA=1, DeratingThermal=1)</Radio>
                  </Stack>
                </RadioGroup>
              </Box>
            )}

            <Box mt={6} borderTopWidth="1px" pt={4}>
              <Text mb={2} fontWeight="bold">
                Firmware Update Simulation:
              </Text>
              <RadioGroup onChange={handleFirmwareSimTypeChange} value={firmwareSimulationType}>
                <Stack>
                  <Radio value="None">None (clear firmware state)</Radio>
                  <Radio value="Pending">Pending (checking for update…)</Radio>
                  <Radio value="Downloading">Downloading</Radio>
                  <Radio value="Installing">Installing</Radio>
                  <Radio value="Success">Success (v2.0.0 installed)</Radio>
                  <Radio value="DownloadFailed">Download Failed</Radio>
                  <Radio value="InstallationFailed">Installation Failed</Radio>
                  <Radio value="FailedChargerNotConnected">Failed — Charger Not Connected</Radio>
                </Stack>
              </RadioGroup>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
